import type { ApiResponse, AuthTokens } from './types';
import { confirmPhotoUpload } from '../photoSafetyConsent';
import { IMAGE_UPLOAD_MAX_BYTES, IMAGE_UPLOAD_MAX_MB } from '../../constants/uploadLimits';
import { isRunningInBiltNativeShell, syncNativeSession } from '../../utils/nativeShell';

export class ApiClient {
  public baseUrl: string;
  public authTokens: AuthTokens | null = null;
  public sessionGeneration = 0;
  public pendingRefresh: Promise<AuthTokens | null> | null = null;
  
  constructor() {
    // For production, the API is on the same domain in the /api/ subfolder.
    // For development, we use the environment variable.
    if (process.env.NODE_ENV === 'production') {
      this.baseUrl = '/api'; // Use a relative path for production
    } else {
      this.baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8090';
    }
    
    this.loadAuthTokens();
  }
  
  protected loadAuthTokens(): void {
    try {
      const stored = localStorage.getItem('built_auth_tokens');
      if (stored) {
        const tokens = JSON.parse(stored);
        // Check if tokens are still valid
        if (new Date(tokens.expiresAt) > new Date()) {
          this.authTokens = tokens;
        } else {
          // Tokens expired, clear them
          this.clearAuthTokens();
        }
      }
    } catch (error) {
      console.error('Failed to load auth tokens:', error);
      this.clearAuthTokens();
    }
  }
  
  public saveAuthTokens(tokens: AuthTokens): void {
    try {
      if (this.authTokens?.user?.id !== tokens.user?.id) this.sessionGeneration += 1;
      this.authTokens = tokens;
      localStorage.setItem('built_auth_tokens', JSON.stringify(tokens));
      if (isRunningInBiltNativeShell() && tokens.user) {
        syncNativeSession({
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          expiresAt: tokens.expiresAt,
          user: tokens.user,
        });
      }
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('bilt-auth-changed', { detail: { authenticated: true } })
        );
      }
    } catch (error) {
      console.error('Failed to save auth tokens:', error);
    }
  }
  
  public clearAuthTokens(): void {
    this.sessionGeneration += 1;
    this.pendingRefresh = null;
    this.authTokens = null;
    localStorage.removeItem('built_auth_tokens');
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('bilt-auth-changed', { detail: { authenticated: false } })
      );
    }
  }
  
  public isProductionHost(): boolean {
    const env = process.env.REACT_APP_ENVIRONMENT;
    if (env === 'production' || env === 'staging') {
      return true;
    }
    if (typeof window !== 'undefined') {
      return /biltspec\.com$/i.test(window.location.hostname);
    }
    return false;
  }
  
  public getAuthHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
  
    if (this.authTokens?.accessToken) {
      headers['Authorization'] = `Bearer ${this.authTokens.accessToken}`;
      if (this.isProductionHost()) {
        headers['X-Auth-Token'] = this.authTokens.accessToken;
      }
    }
  
    return headers;
  }
  
  /** For components that call fetch() directly instead of makeRequest */
  getAuthenticatedFetchHeaders(): Record<string, string> {
    return this.getAuthHeaders() as Record<string, string>;
  }
  
  public async handleResponse<T>(response: Response, requestGeneration = this.sessionGeneration): Promise<ApiResponse<T>> {
    let data: any;
  
    try {
      const contentType = response.headers.get('content-type');
      const rawBody = await response.text();
      const trimmed = rawBody.trim();
      const looksJson =
        trimmed.startsWith('{') ||
        trimmed.startsWith('[') ||
        (contentType != null && contentType.includes('application/json'));

      if (looksJson) {
        if (!trimmed) {
          data = {
            message: `Empty server response (HTTP ${response.status})`,
            error: `Empty server response (HTTP ${response.status})`,
          };
        } else {
          data = JSON.parse(trimmed);
        }
      } else {
        const fallback = trimmed || `Unexpected response (HTTP ${response.status})`;
        data = { message: fallback, error: fallback };
      }
    } catch (error) {
      console.error('Failed to parse response:', error);
      data = { message: 'Invalid response format', error: 'Invalid response format' };
    }
  
    if (requestGeneration !== this.sessionGeneration) {
      throw Object.assign(new Error('Session changed'), { code: 'session_changed' });
    }
    if (!response.ok) {
      // If the response is JSON and success:true, treat as success
      if (data && data.success) {
        return data;
      }
      // Handle authentication errors - but be more conservative about clearing tokens
      if (response.status === 401) {
        console.warn('401 Unauthorized response received:', {
          url: response.url,
          status: response.status,
          message: data.message || data.error
        });
        // Only clear tokens if we're sure it's an auth issue, not a temporary server problem
        // Check if the error message indicates invalid/expired token specifically
        const errorMessage = (data.message || data.error || '').toLowerCase();
        const isDefinitelyAuthError = errorMessage.includes('token') || 
                                     errorMessage.includes('unauthorized') || 
                                     errorMessage.includes('expired') ||
                                     errorMessage.includes('invalid');
        if (isDefinitelyAuthError) {
          this.handleTokenExpiration('invalid');
        } else {
        }
      }
      throw Object.assign(
        new Error(data.error || data.message || `HTTP ${response.status}: ${response.statusText}`),
        {
          code: data.code,
          incomplete_classes: data.incomplete_classes,
          reasons: data.reasons,
          eligibility: data.eligibility,
          status: response.status,
          // Some error responses carry a usable payload (a 409 duplicate names the record
          // that already exists), which was dropped before callers could read it.
          data: data.data,
        }
      );
    }
  
    return data;
  }
  
  /** Overridden by authMethods at runtime via Object.assign */
  async refreshToken(): Promise<AuthTokens | null> {
    return null;
  }

  public async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {},
    isRetry = false
  ): Promise<ApiResponse<T>> {
    const requestGeneration = this.sessionGeneration;
    let url = `${this.baseUrl}${endpoint}`;
  
    // For FormData requests, don't set Content-Type header
    const isFormData = options.body instanceof FormData;
    if (isFormData) {
      await confirmPhotoUpload(options.body as FormData);
      if (requestGeneration !== this.sessionGeneration) throw new Error('Session changed');
    }
    let headers: Record<string, string> = {};
    
    if (isFormData) {
      if (this.authTokens?.accessToken) {
        headers['Authorization'] = `Bearer ${this.authTokens.accessToken}`;
        if (this.isProductionHost()) {
          headers['X-Auth-Token'] = this.authTokens.accessToken;
        }
      }
    } else {
      headers = this.getAuthHeaders() as Record<string, string>;
    }
  
    const requestOptions: RequestInit = {
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
    };
  
    const skipTokenRefresh =
      endpoint.includes('login.php') ||
      endpoint.includes('register.php') ||
      endpoint.includes('auth/refresh.php') || endpoint.includes('auth/logout.php');
  
    try {
      const response = await fetch(url, requestOptions);
      if (requestGeneration !== this.sessionGeneration && !endpoint.includes('auth/logout.php')) {
        throw Object.assign(new Error('Session changed'), { code: 'session_changed' });
      }
  
      if (
        response.status === 401 &&
        !isRetry &&
        !skipTokenRefresh &&
        this.authTokens?.accessToken
      ) {
        const refreshed = await this.refreshToken();
        if (refreshed && requestGeneration === this.sessionGeneration) {
          return this.makeRequest<T>(endpoint, options, true);
        }
      }
  
      return await this.handleResponse<T>(response, requestGeneration);
    } catch (error) {
      console.error(`API request failed: ${endpoint}`, error);
      throw error;
    }
  }
  
  
  // Utility methods
  // Enhanced authentication methods with proactive validation
  private checkTokenExpiration(): { isExpired: boolean; timeUntilExpiry: number; shouldWarn: boolean } {
    if (!this.authTokens) {
      return { isExpired: true, timeUntilExpiry: 0, shouldWarn: false };
    }
    
    const expirationDate = new Date(this.authTokens.expiresAt);
    const currentDate = new Date();
    const timeUntilExpiry = expirationDate.getTime() - currentDate.getTime();
    const isExpired = timeUntilExpiry <= 0;
    
    // Warn user if token expires in less than 5 minutes
    const shouldWarn = timeUntilExpiry > 0 && timeUntilExpiry < 5 * 60 * 1000;
    
    return { isExpired, timeUntilExpiry, shouldWarn };
  }
  
  private handleTokenExpiration(reason: 'expired' | 'invalid' | 'server_error'): void {
    // Clear tokens
    this.clearAuthTokens();
    
    // Dispatch auth expired event with reason
    window.dispatchEvent(new CustomEvent('auth-expired', { 
      detail: { reason, timestamp: new Date().toISOString() }
    }));
    
    // Show user notification if we're in a browser environment
    if (typeof window !== 'undefined' && window.Notification && Notification.permission === 'granted') {
      new Notification('Session Expired', {
        body: 'Your session has expired. Please log in again.',
        icon: '/logo192.png'
      });
    }
  }
  
  // Enhanced isAuthenticated with proactive expiration check
  isAuthenticated(): boolean {
    if (!this.authTokens) {
      return false;
    }
    
    const { isExpired, shouldWarn } = this.checkTokenExpiration();
    
    if (isExpired) {
      this.handleTokenExpiration('expired');
      return false;
    }
    
    if (shouldWarn) {
      // Dispatch warning event for UI to show countdown
      window.dispatchEvent(new CustomEvent('auth-expiring-soon', { 
        detail: { 
          expiresAt: this.authTokens.expiresAt,
          timeUntilExpiry: new Date(this.authTokens.expiresAt).getTime() - new Date().getTime()
        }
      }));
    }
    
    return true;
  }
  
  getAccessToken(): string | null {
    return this.authTokens?.accessToken || null;
  }
  
  getCurrentUser(): any | null {
    return this.authTokens?.user || null;
  }

  getStoredAuthTokens(): AuthTokens | null {
    return this.authTokens;
  }
  
  // Update stored user data (useful when profile changes like avatar upload)
  updateStoredUserData(updatedUserData: any): void {
    if (this.authTokens?.user) {
      this.authTokens.user = { ...this.authTokens.user, ...updatedUserData };
      this.saveAuthTokens(this.authTokens);
    }
  }
  
  getAdminEndpoint(path: string): string {
    return path;
  }

  // Helper method to get full URL for uploaded files
  getFileUrl(relativePath: string | null): string | null {
    if (!relativePath) return null;

    // If it's already a full URL, return as-is
    if (relativePath.startsWith('http://') || relativePath.startsWith('https://')) {
      return relativePath;
    }

    // Demo seed + other site-root static assets must not be prefixed with /api
    const clean = relativePath.replace(/^\/+/, '');
    if (
      clean.startsWith('seed/') ||
      clean.startsWith('brand/') ||
      clean.startsWith('static/')
    ) {
      return `/${clean}`;
    }

    // Convert relative path to full URL using backend base URL
    // Ensure there's always a slash between baseUrl and relativePath
    const separator = this.baseUrl.endsWith('/') || relativePath.startsWith('/') ? '' : '/';
    return `${this.baseUrl}${separator}${relativePath}`;
  }
  
  // Input sanitization methods
  public sanitizeModificationData(modification: any): any {
    const sanitized: any = {
      title: this.normalizeModificationText(modification.title),
      description: this.normalizeModificationText(modification.description),
      part_number: this.normalizeModificationText(modification.part_number),
      cost: this.sanitizeNumber(modification.cost),
      install_date: this.sanitizeDate(modification.install_date),
      notes: this.normalizeModificationText(modification.notes),
      // Handle specifications specially - don't over-sanitize
      specifications: modification.specifications,
    };
  
    // Handle category - can be either category_id (number) or category (string)
    if (!modification.category && modification.category_id !== undefined) {
      sanitized.category_id = this.sanitizeNumber(modification.category_id);
    } else if (modification.category !== undefined) {
      sanitized.category = this.normalizeModificationText(modification.category);
    }
  
    // Handle vendor - can be either vendor_id (number) or vendor (string)
    if (!modification.vendor && modification.vendor_id !== undefined) {
      sanitized.vendor_id = this.sanitizeNumber(modification.vendor_id);
    } else if (modification.vendor !== undefined) {
      sanitized.vendor = this.normalizeModificationText(modification.vendor);
    }
  
    // Handle parent modification relationship
    if (modification.parent_modification_id !== undefined) {
      sanitized.parent_modification_id = modification.parent_modification_id == null || modification.parent_modification_id === ''
        ? null : this.sanitizeNumber(modification.parent_modification_id);
    }
  
    // Preserve parentComponent (title string) for backend lookup
    if (modification.parentComponent !== undefined) {
      sanitized.parentComponent = this.normalizeModificationText(modification.parentComponent);
    }
  
    // Handle performance fields
    if (modification.power_gain !== undefined) {
      sanitized.power_gain = this.sanitizeNumber(modification.power_gain);
    }
    if (modification.torque_gain !== undefined) {
      sanitized.torque_gain = this.sanitizeNumber(modification.torque_gain);
    }
    if (modification.weight_change !== undefined) {
      sanitized.weight_change = this.sanitizeNumber(modification.weight_change);
    }
  
    return sanitized;
  }
  
  private normalizeModificationText(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }

  public sanitizeImageData(imageData: any): any {
    return {
      image_url: this.sanitizeUrl(imageData.src || imageData.image_url),
      alt_text: this.sanitizeString(imageData.alt),
      title: this.sanitizeString(imageData.title),
      sort_order: this.sanitizeNumber(imageData.sort_order),
    };
  }
  
  public sanitizeString(value: any): string {
    if (typeof value !== 'string') return '';
  
    // Remove HTML tags and dangerous characters
    return value
      .replace(/<[^>]*>/g, '') // Remove HTML tags
      .replace(/[<>'"&]/g, '') // Remove dangerous characters
      .trim()
      .substring(0, 1000); // Limit length
  }
  
  public sanitizeNumber(value: any): number {
    const num = parseFloat(value);
    return isNaN(num) ? 0 : Math.max(0, num);
  }
  
  public sanitizeDate(value: any): string | null {
    if (!value) return null;
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date.toISOString().split('T')[0];
  }
  
  public sanitizeUrl(value: any): string {
    if (typeof value !== 'string') return '';
  
    try {
      const url = new URL(value);
      // Only allow http and https protocols
      if (url.protocol !== 'http:' && url.protocol !== 'https:') {
        return '';
      }
      return url.toString();
    } catch {
      return '';
    }
  }
  
  public sanitizeObject(value: any): any {
    if (typeof value !== 'object' || value === null) return {};
  
    const sanitized: any = {};
    for (const [key, val] of Object.entries(value)) {
      if (typeof key === 'string' && key.length <= 100) {
        // For specifications, preserve the original value structure
        // Only sanitize if it's a string, otherwise keep as-is
        if (typeof val === 'string') {
          sanitized[this.sanitizeString(key)] = this.sanitizeString(String(val));
        } else {
          sanitized[this.sanitizeString(key)] = val;
        }
      }
    }
    return sanitized;
  }
  
  public sanitizePerformanceData(data: any): any {
    // Sanitize and validate performance data
    const sanitized = { ...data };
  
    // Ensure numeric values are properly formatted
    if (sanitized.horsepower) sanitized.horsepower = Number(sanitized.horsepower);
    if (sanitized.torque) sanitized.torque = Number(sanitized.torque);
    if (sanitized.quarterMileTime) sanitized.quarterMileTime = Number(sanitized.quarterMileTime);
    if (sanitized.zeroToSixty) sanitized.zeroToSixty = Number(sanitized.zeroToSixty);
    if (sanitized.lapTime) sanitized.lapTime = Number(sanitized.lapTime);
    if (sanitized.fuelEconomy) sanitized.fuelEconomy = Number(sanitized.fuelEconomy);
  
    // Ensure dates are properly formatted
    if (sanitized.testDate) {
      sanitized.testDate = new Date(sanitized.testDate).toISOString();
    }
  
    return sanitized;
  }
  
  public validateImageFile(file: File): void {
    // Check file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      throw new Error('Invalid file type. Only JPEG, PNG, WebP, and GIF images are allowed.');
    }
  
    // Check file size (shared image upload cap)
    if (file.size > IMAGE_UPLOAD_MAX_BYTES) {
      throw new Error(`File size too large. Maximum size is ${IMAGE_UPLOAD_MAX_MB}MB.`);
    }
  }
  
  async getCountries(): Promise<any[]> {
    try {
      const response = await this.makeRequest<any>('/countries.php', {
        method: 'GET',
      });
  
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.error || 'Failed to fetch countries');
      }
    } catch (error: any) {
      console.error('Get countries error:', error);
      throw new Error(error.message || 'Failed to fetch countries. Please try again.');
    }
  }
  
  async getStatesProvinces(countryCode: string): Promise<any[]> {
    try {
      const response = await this.makeRequest<any>(`/states_provinces.php?country=${encodeURIComponent(countryCode)}`, {
        method: 'GET',
      });
  
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.error || 'Failed to fetch states/provinces');
      }
    } catch (error: any) {
      console.error('Get states/provinces error:', error);
      throw new Error(error.message || 'Failed to fetch states/provinces. Please try again.');
    }
  }
}
